/**
 * Session token storage + wallet sign-in flow.
 *
 * Shieldz Payments uses Sign-In-With-Ethereum (EIP-4361). The dashboard
 * is web-first; merchants connect any EVM wallet (injected via
 * window.ethereum) and sign a server-issued nonce to mint a session.
 *
 * The session token lives in localStorage under `shieldz.session`. It's a
 * Bearer credential; the backend stores SHA-256(token) hex and matches
 * on lookup. The token is rotated only on explicit logout — for V1 there
 * is no auto-refresh.
 *
 * This module is intentionally side-effect free: it doesn't render UI,
 * doesn't navigate. Callers (AuthGate, LoginScreen) decide the UX.
 */

import type { LocalAccount } from "viem";
import { loginAccountFromSecret } from "./key-gen";

const STORAGE_KEY = "shieldz.session";

const API_BASE: string =
  (import.meta.env.VITE_SHIELDZ_API_URL as string | undefined) ??
  "https://shieldz.cash";

export interface ShieldzAccount {
  id: number;
  // Null for social-login accounts (Google / Telegram / email) — only
  // wallet (SIWE) accounts carry an EVM address as their identity.
  evm_address: string | null;
  email: string | null;
  email_verified: boolean;
  created_at: number;
  last_login_at?: number | null;
}

export interface MerchantSummary {
  id: number;
  display_name: string;
  logo_url: string | null;
  default_settlement: { chain: string; asset: string; address: string };
  slippage_bps: number;
  enabled: boolean;
  setup_verified: boolean;
  telegram_dm_enabled: boolean;
  created_at: number;
}

export interface MeResponse {
  account: ShieldzAccount;
  merchant: MerchantSummary | null;
}

export class SignInError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SignInError";
  }
}

export const getStoredSession = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const setStoredSession = (token: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // private mode etc — swallow
  }
};

export const clearStoredSession = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* swallow */
  }
};

interface InjectedProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  isMetaMask?: boolean;
  isRabby?: boolean;
}

const getProvider = (): InjectedProvider | null => {
  const w = window as unknown as { ethereum?: InjectedProvider };
  return w.ethereum ?? null;
};

export const hasInjectedWallet = (): boolean => getProvider() !== null;

/** True on a phone/tablet browser (coarse UA sniff, good enough to decide
 *  whether to offer the "open in wallet app" fallback). */
export const isMobileBrowser = (): boolean =>
  typeof navigator !== "undefined" &&
  /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);

/**
 * Mobile browsers (Safari/Chrome) don't inject window.ethereum, so the SIWE
 * flow can't reach a wallet. Each wallet app ships a universal link that
 * reopens a URL inside its own in-app browser, where window.ethereum DOES
 * exist and the normal flow works. We deep-link back to this exact page so
 * the merchant lands on login again, now inside the wallet, and taps Connect.
 */
export interface WalletDeepLink {
  id: string;
  name: string;
  url: string;
}

export const walletDeepLinks = (): WalletDeepLink[] => {
  if (typeof window === "undefined") return [];
  const full = window.location.href;
  const hostPath = window.location.host + window.location.pathname + window.location.search;
  const enc = encodeURIComponent(full);
  return [
    // MetaMask expects host+path WITHOUT a scheme after /dapp/.
    { id: "metamask", name: "MetaMask", url: `https://metamask.app.link/dapp/${hostPath}` },
    { id: "coinbase", name: "Coinbase Wallet", url: `https://go.cb-w.com/dapp?cb_url=${enc}` },
    { id: "trust", name: "Trust Wallet", url: `https://link.trustwallet.com/open_url?url=${enc}` },
    { id: "rainbow", name: "Rainbow", url: `https://rnbwapp.com/dapp?url=${enc}` },
  ];
};

const fetchJSON = async <T>(
  path: string,
  init: RequestInit = {},
): Promise<T> => {
  const url = `${API_BASE.replace(/\/+$/, "")}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* not JSON */
    }
    if (
      body &&
      typeof body === "object" &&
      "error" in body &&
      body.error &&
      typeof body.error === "object"
    ) {
      const e = body.error as { code?: string; message?: string };
      throw new SignInError(e.code ?? "unknown_error", e.message ?? `HTTP ${res.status}`);
    }
    throw new SignInError("http_error", `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
};

/**
 * Run the full sign-in flow with the user's injected wallet:
 *   1. eth_requestAccounts → user approves connection
 *   2. POST /api/auth/nonce → server issues nonce + canonical message
 *   3. personal_sign → user approves signature
 *   4. POST /api/auth/verify → server verifies, returns session_token
 *
 * On success: persists the token in localStorage and returns the account.
 * Throws SignInError with a stable code on failure (`user_rejected`,
 * `no_wallet`, `bad_signature`, etc.).
 */
export const signInWithWallet = async (): Promise<{
  account: ShieldzAccount;
  token: string;
}> => {
  const provider = getProvider();
  if (!provider) {
    throw new SignInError(
      "no_wallet",
      "No browser wallet found. Install MetaMask, or sign in with a keystore or seed phrase.",
    );
  }

  // 1) request accounts
  let accounts: string[];
  try {
    accounts = (await provider.request({ method: "eth_requestAccounts" })) as string[];
  } catch (e) {
    const code = (e as { code?: number }).code;
    if (code === 4001) throw new SignInError("user_rejected", "Connection rejected");
    throw new SignInError("connect_failed", "Could not connect to wallet");
  }
  const address = accounts[0];
  if (!address) throw new SignInError("no_account", "Wallet returned no accounts");

  return completeSiwe(address, async (message) => {
    try {
      return (await provider.request({
        method: "personal_sign",
        params: [message, address],
      })) as string;
    } catch (e) {
      const code = (e as { code?: number }).code;
      if (code === 4001) throw new SignInError("user_rejected", "Signature rejected");
      throw new SignInError("sign_failed", "Wallet failed to sign the message");
    }
  });
};

/**
 * Shared SIWE tail used by every sign-in path:
 *   1. POST /api/auth/nonce {address} → server issues nonce + canonical message
 *   2. `sign(message)` → an EIP-191 personal_sign signature (wallet or keystore)
 *   3. POST /api/auth/verify {message, signature} → server recovers the signer,
 *      auto-creates the account on first sign-in, returns a session token
 *
 * The server is signer-agnostic — it only verifies that the signature recovers
 * to the claimed address — so a keystore-derived in-browser signer is treated
 * exactly like an injected wallet. Persists the token and returns the account.
 */
const completeSiwe = async (
  address: string,
  sign: (message: string) => Promise<string>,
): Promise<{ account: ShieldzAccount; token: string }> => {
  const { message } = await fetchJSON<{
    nonce: string;
    message: string;
    issued_at: string;
    address: string;
  }>("/api/auth/nonce", {
    method: "POST",
    body: JSON.stringify({ address }),
  });

  const signature = await sign(message);

  const r = await fetchJSON<{
    session_token: string;
    account: ShieldzAccount;
  }>("/api/auth/verify", {
    method: "POST",
    body: JSON.stringify({ message, signature }),
  });

  setStoredSession(r.session_token);
  return { account: r.account, token: r.session_token };
};

/**
 * Sign in with a keystore file or a raw seed phrase — no browser wallet needed.
 * Accepts our Shieldz keystore JSON, a standard Web3 V3 keystore (MetaMask/Geth/
 * ethers), or a 12-word BIP-39 phrase. The secret is decrypted in-browser, signs
 * the SIWE nonce locally, and is never sent anywhere; only the signature goes to
 * the server. The login identity is the EVM key at m/44'/60'/0'/0/0 (keystore/
 * phrase) — the same address "Connect Wallet" with that key would use.
 */
export const signInWithSecret = async (input: {
  keystore?: Record<string, unknown>;
  mnemonic?: string;
  passphrase?: string;
}): Promise<{ account: ShieldzAccount; token: string }> => {
  let account: LocalAccount;
  try {
    account = await loginAccountFromSecret(input);
  } catch (e) {
    throw new SignInError(
      "bad_keystore",
      e instanceof Error ? e.message : "Could not read that keystore.",
    );
  }
  return completeSiwe(account.address, (message) => account.signMessage({ message }));
};

/**
 * Social / email login completers. Each mirrors completeSiwe's tail: it POSTs
 * the provider proof to the matching /api/auth/* endpoint, receives the same
 * { session_token, account } shape, persists the token, and returns. The
 * dashboard treats every provider uniformly from here on.
 */
const completeProvider = async (
  path: string,
  body: Record<string, unknown>,
): Promise<{ account: ShieldzAccount; token: string }> => {
  const r = await fetchJSON<{ session_token: string; account: ShieldzAccount }>(path, {
    method: "POST",
    body: JSON.stringify(body),
  });
  setStoredSession(r.session_token);
  return { account: r.account, token: r.session_token };
};

/** Google Identity Services: `accessToken` is the GIS token-client access
 *  token (custom button). The backend verifies it via Google's tokeninfo. */
export const completeGoogle = (
  accessToken: string,
): Promise<{ account: ShieldzAccount; token: string }> =>
  completeProvider("/api/auth/google", { access_token: accessToken });

/** Telegram Login Widget: pass the widget's user payload verbatim. */
export const completeTelegram = (
  widgetUser: Record<string, unknown>,
): Promise<{ account: ShieldzAccount; token: string }> =>
  completeProvider("/api/auth/telegram", widgetUser);

/** The redirect URI Discord returns to. Must match a URI registered on the
 *  Discord app exactly. We come back to the current page (login or signup). */
export const discordRedirectUri = (): string =>
  typeof window === "undefined" ? "" : window.location.origin + window.location.pathname;

/**
 * Discord OAuth2 — step 1: send the browser to Discord's authorize page. A
 * random `state` is stored so the callback can be verified against CSRF.
 */
export const startDiscord = (clientId: string): void => {
  if (typeof window === "undefined") return;
  const state = crypto.randomUUID();
  sessionStorage.setItem("shieldz.discord.state", state);
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: "code",
    redirect_uri: discordRedirectUri(),
    scope: "identify email",
    state,
    prompt: "consent",
  });
  window.location.href = `https://discord.com/api/oauth2/authorize?${params.toString()}`;
};

/**
 * Discord OAuth2 — step 2: called on the callback page load when `?code` is
 * present. Verifies `state`, exchanges the code server-side, returns a session.
 * Returns null when there is no Discord callback in the URL (nothing to do).
 */
export const completeDiscordCallback = async (): Promise<
  { account: ShieldzAccount; token: string } | null
> => {
  if (typeof window === "undefined") return null;
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code || !state) return null;
  const expected = sessionStorage.getItem("shieldz.discord.state");
  // Clean the code/state out of the URL either way so a refresh can't replay.
  const redirectUri = window.location.origin + window.location.pathname;
  url.searchParams.delete("code");
  url.searchParams.delete("state");
  window.history.replaceState({}, "", url.toString());
  if (!expected || expected !== state) {
    throw new SignInError("discord_state_mismatch", "Discord sign-in expired, please try again.");
  }
  sessionStorage.removeItem("shieldz.discord.state");
  return completeProvider("/api/auth/discord", { code, redirect_uri: redirectUri });
};

/** Email passwordless — step 1: request a code. Always resolves (server 200). */
export const completeEmailStart = async (email: string): Promise<void> => {
  await fetchJSON("/api/auth/email/start", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
};

/** Email passwordless — step 2: verify the code → session. */
export const completeEmailVerify = (
  email: string,
  code: string,
): Promise<{ account: ShieldzAccount; token: string }> =>
  completeProvider("/api/auth/email/verify", { email, code });

export const fetchMe = async (): Promise<MeResponse | null> => {
  const token = getStoredSession();
  if (!token) return null;
  try {
    return await fetchJSON<MeResponse>("/api/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (e) {
    if (e instanceof SignInError && e.code === "unauthenticated") {
      clearStoredSession();
      return null;
    }
    throw e;
  }
};

export const signOut = async (): Promise<void> => {
  const token = getStoredSession();
  if (token) {
    try {
      await fetchJSON("/api/auth/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      /* server logout is best-effort */
    }
  }
  clearStoredSession();
};
